export interface BestMathResult {
  bestMatch: string;
  bestMatchSentenceIndex: number;
  start: number;
  end: number;
  type: 'exact' | 'prefix' | 'subword' | 'typo';
}

export const maxUserSearchLength = 2; // for optimization purposes this should never exceed 3 !!!

export function findBestMatches(sentences: string[], rawSearchTerms: string[]): BestMathResult[] {
  const searchTerms = rawSearchTerms.map((term) => term.toLowerCase());
  const seen = new Set();
  const matches: {
    bestMatch: string;
    bestMatchSentenceIndex: number;
    start: number;
    end: number;
    type: 'exact' | 'prefix' | 'subword' | 'typo';
  }[] = [];

  for (let i = 0; i < sentences.length; i++) {
    const original = sentences[i];
    const lower = original.toLowerCase();
    const words = lower.split(/[^\w-']+/).filter(Boolean);

    let sentenceHadMatch = false;

    for (let searchTerm of searchTerms) {
      // Word-level strategy
      for (let word of words) {
        const wordIndex = lower.indexOf(word); // safe since it's the original `lower`
        const endIndex = wordIndex + word.length;

        // Exact match
        if (word === searchTerm) {
          const key = `exact-${i}-${wordIndex}`;
          if (!seen.has(key)) {
            seen.add(key);
            matches.push({
              bestMatch: original.substring(wordIndex, endIndex),
              bestMatchSentenceIndex: i,
              start: wordIndex,
              end: endIndex,
              type: 'exact',
            });
          }
          sentenceHadMatch = true;
          continue;
        }

        // Prefix match
        if (word.startsWith(searchTerm)) {
          const key = `prefix-${i}-${wordIndex}`;
          if (!seen.has(key)) {
            seen.add(key);
            matches.push({
              bestMatch: original.substring(wordIndex, endIndex),
              bestMatchSentenceIndex: i,
              start: wordIndex,
              end: endIndex,
              type: 'prefix',
            });
          }
          sentenceHadMatch = true;
          continue;
        }

        // Subword match (search term inside word but not at the beginning)
        const subIndex = word.indexOf(searchTerm);
        if (subIndex > 0) {
          const subStart = wordIndex + subIndex;
          const subEnd = subStart + searchTerm.length;
          const key = `subword-${i}-${subStart}`;
          if (!seen.has(key)) {
            seen.add(key);
            matches.push({
              bestMatch: original.substring(subStart, subEnd),
              bestMatchSentenceIndex: i,
              start: subStart,
              end: subEnd,
              type: 'subword',
            });
          }
          sentenceHadMatch = true;
          continue;
        }
      }

      // Fallback strategy: Typo/fuzzy match (only if no match found)
      if (!sentenceHadMatch && searchTerm.length > 4) {
        for (let word of words) {
          if (word.length <= 4) continue;

          const { pass } = checkMatching(word, searchTerm);

          if (pass) {
            const wordIndex = lower.indexOf(word);
            const endIndex = wordIndex + word.length;
            const key = `typo-${i}-${wordIndex}`;
            if (!seen.has(key)) {
              seen.add(key);
              matches.push({
                bestMatch: original.substring(wordIndex, endIndex),
                bestMatchSentenceIndex: i,
                start: wordIndex,
                end: endIndex,
                type: 'typo',
              });
            }
          }
        }
      }
    }
  }

  const typePriority = { exact: 4, prefix: 3, subword: 2, typo: 1 };

  return matches.sort(
    (a, b) =>
      typePriority[b.type] - typePriority[a.type] ||
      b.bestMatch.length - a.bestMatch.length ||
      a.start - b.start,
  );
}

type MatchPercentage = 0.7 | 0.8 | 0.9;
// Utility to count overlapping characters
export const checkMatching = (
  existingText: string,
  textToCompare: string,
  threshold: MatchPercentage = 0.7,
  maxLengthDifference: number = 3,
): { similarity: number; pass: boolean } => {
  // Early return if length difference exceeds the allowed maximum
  if (Math.abs(existingText.length - textToCompare.length) > maxLengthDifference) {
    return { similarity: 0, pass: false };
  }

  // Normalize case
  const text1 = existingText.toLowerCase();
  const text2 = textToCompare.toLowerCase();

  // Calculate Levenshtein distance
  const matrix: number[][] = [];

  // Initialize matrix
  for (let i = 0; i <= text1.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= text2.length; j++) {
    matrix[0][j] = j;
  }

  // Fill the matrix
  for (let i = 1; i <= text1.length; i++) {
    for (let j = 1; j <= text2.length; j++) {
      if (text1[i - 1] === text2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1, // deletion
        );
      }
    }
  }

  // Calculate similarity as a percentage (1 minus normalized distance)
  const distance = matrix[text1.length][text2.length];
  const maxLength = Math.max(text1.length, text2.length);
  const similarity = maxLength > 0 ? 1 - distance / maxLength : 1;

  return {
    similarity,
    pass: similarity >= threshold,
  };
};
